import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { format } from "date-fns";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserCheck, Download, Eye, IndianRupee, Loader2, CalendarDays, Pill, Undo2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const fmtRs = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const monthLabel = (m) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;

export default function Attendance() {
  const { user } = useAuth();
  const [month, setMonth] = useState(format(new Date(), "yyyy-MM"));
  const [summary, setSummary] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [detailFor, setDetailFor] = useState(null);
  const [detail, setDetail] = useState([]);
  const [markDate, setMarkDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [markStatus, setMarkStatus] = useState("present");
  const [slip, setSlip] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = () => {
    api.get("/attendance/summary", { params: { month } }).then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/payroll", { params: { month } }).then(({ data }) => setPayroll(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const openDetail = (row) => {
    setDetailFor(row);
    const todayStr = format(new Date(), "yyyy-MM-dd");
    setMarkDate(todayStr.startsWith(month) ? todayStr : `${month}-01`);
    api.get("/attendance", { params: { month, user_id: row.user_id } }).then(({ data }) => setDetail(data));
  };

  const markManual = async (st) => {
    const status = st || markStatus;
    try {
      await api.post("/attendance/mark", { user_id: detailFor.user_id, date: markDate, status });
      toast.success(`Marked ${status.replace("_", " ")} on ${markDate}`);
      openDetail(detailFor);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/payroll/generate", { month });
      toast.success(`Salary generated for ${data.generated} staff — ${monthLabel(month)}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setGenerating(false);
    }
  };

  const markPaid = async (row) => {
    if (!window.confirm(`Mark ${row.name}'s salary ${fmtRs(row.payable)} as PAID?`)) return;
    try {
      await api.post(`/payroll/${row.payroll_id}/pay`);
      toast.success("Marked as paid");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const markUnpaid = async (row) => {
    if (!window.confirm(`Mark ${row.name}'s salary as UNPAID (pending) again? Admin-only action.`)) return;
    try {
      await api.post(`/payroll/${row.payroll_id}/unpaid`);
      toast.success("Marked as unpaid (pending)");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const openSlip = async (row) => {
    try {
      const { data } = await api.get(`/payroll/slip/${row.user_id}`, { params: { month } });
      setSlip(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="attendance-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
            <UserCheck className="w-6 h-6 text-emerald-700" /> Attendance &amp; Salary
          </h1>
          <p className="text-sm text-slate-500">Attendance is auto-marked on login. Salary is auto-computed at month end.</p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-slate-400" />
          <Input data-testid="attendance-month" type="month" value={month} max={format(new Date(), "yyyy-MM")} onChange={(e) => setMonth(e.target.value)} className="h-11 w-44" />
        </div>
      </div>

      {/* Attendance summary */}
      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-100">
          <h3 className="font-display font-bold">Attendance — {monthLabel(month)}</h3>
          <p className="text-xs text-slate-500">P = Present (login), H = Half day, L = Leave, A = Absent</p>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="attendance-table">
            <TableHeader><TableRow>
              <TableHead>Staff</TableHead><TableHead>Role</TableHead>
              <TableHead className="text-center">P</TableHead><TableHead className="text-center">H</TableHead>
              <TableHead className="text-center">L</TableHead><TableHead className="text-center">A</TableHead>
              <TableHead className="text-right">Detail</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {summary.map((s) => (
                <TableRow key={s.user_id} data-testid={`att-row-${s.user_id}`}>
                  <TableCell>
                    <button onClick={() => openDetail(s)} className="text-left group" data-testid={`att-name-${s.user_id}`}>
                      <div className="font-semibold group-hover:text-emerald-800 group-hover:underline underline-offset-2">{s.name}</div>
                      <div className="text-xs text-slate-500">{s.email}</div>
                    </button>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="capitalize">{s.role}</Badge></TableCell>
                  <TableCell className="text-center font-mono-num font-bold text-emerald-700">{s.present}</TableCell>
                  <TableCell className="text-center font-mono-num text-amber-600">{s.half_day}</TableCell>
                  <TableCell className="text-center font-mono-num text-sky-700">{s.leave}</TableCell>
                  <TableCell className="text-center font-mono-num font-bold text-red-600">{s.absent}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" data-testid={`att-detail-${s.user_id}`} onClick={() => openDetail(s)} className="h-8">
                      <Eye className="w-3.5 h-3.5 mr-1" /> Calendar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {summary.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No staff found</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Salary / Payroll */}
      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h3 className="font-display font-bold">Salary — {monthLabel(month)}</h3>
            <p className="text-xs text-slate-500">Salary = (monthly salary ÷ days in month) × (present + half day × 0.5)</p>
          </div>
          <Button onClick={generate} disabled={generating} data-testid="generate-payroll-btn" className="bg-emerald-900 hover:bg-emerald-800">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <IndianRupee className="w-4 h-4 mr-2" />}
            Generate Salary
          </Button>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="payroll-table">
            <TableHeader><TableRow>
              <TableHead>Staff</TableHead><TableHead className="text-right">Monthly Salary</TableHead>
              <TableHead className="text-center">Payable Days</TableHead><TableHead className="text-right">Payable</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {payroll.map((p) => (
                <TableRow key={p.user_id} data-testid={`payroll-row-${p.user_id}`}>
                  <TableCell>
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-xs text-slate-500 capitalize">{p.role}</div>
                  </TableCell>
                  <TableCell className="text-right font-mono-num">{p.salary > 0 ? fmtRs(p.salary) : <span className="text-slate-400">Not set</span>}</TableCell>
                  <TableCell className="text-center font-mono-num">{p.present + p.half_day * 0.5}</TableCell>
                  <TableCell className="text-right font-mono-num font-bold text-emerald-900">{fmtRs(p.payable)}</TableCell>
                  <TableCell>
                    {p.pay_status === "paid"
                      ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Paid {p.paid_at ? `on ${new Date(p.paid_at).toLocaleDateString("en-IN")}` : ""}</Badge>
                      : <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Pending</Badge>}
                  </TableCell>
                  <TableCell className="text-right space-x-1.5">
                    <Button size="sm" variant="outline" data-testid={`slip-btn-${p.user_id}`} onClick={() => openSlip(p)} className="h-8">
                      <Download className="w-3.5 h-3.5 mr-1" /> Slip
                    </Button>
                    {p.pay_status !== "paid" && p.payroll_id && (
                      <Button size="sm" data-testid={`pay-btn-${p.user_id}`} onClick={() => markPaid(p)} className="h-8 bg-emerald-900 hover:bg-emerald-800">Mark Paid</Button>
                    )}
                    {p.pay_status === "paid" && p.payroll_id && user?.role === "admin" && (
                      <Button size="sm" variant="outline" data-testid={`unpay-btn-${p.user_id}`} onClick={() => markUnpaid(p)} className="h-8 border-amber-300 text-amber-700 hover:bg-amber-50">
                        <Undo2 className="w-3.5 h-3.5 mr-1" /> Mark Unpaid
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Attendance calendar dialog */}
      <Dialog open={!!detailFor} onOpenChange={(o) => !o && setDetailFor(null)}>
        <DialogContent className="max-w-lg bg-white" data-testid="attendance-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{detailFor?.name} — {monthLabel(month)}</DialogTitle>
          </DialogHeader>
          {detailFor && (
            <AttendanceCalendar
              month={month}
              detail={detail}
              daysInMonth={detailFor.days_in_month}
              selectedDate={markDate}
              onPickDay={(d) => setMarkDate(d)}
              onQuickMark={(st) => markManual(st)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Salary slip dialog (printable) */}
      <Dialog open={!!slip} onOpenChange={(o) => !o && setSlip(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="salary-slip-dialog">
          <DialogHeader><DialogTitle className="font-display">Salary Slip</DialogTitle></DialogHeader>
          {slip && (
            <div className="print-area">
              <div className="border border-slate-200 rounded-lg p-5 bg-white">
                <div className="text-center border-b border-dashed border-slate-300 pb-3">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-emerald-900 flex items-center justify-center"><Pill className="w-4 h-4 text-white" /></div>
                    <div className="font-display font-extrabold text-emerald-950">{slip.company?.pharmacy_name || "Trustmeds Pharma"}</div>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">Salary Slip — {monthLabel(slip.month)}</div>
                </div>
                <div className="mt-3 text-sm space-y-1">
                  <Row2 l="Employee" v={`${slip.user.name} (${slip.user.role})`} />
                  <Row2 l="Month" v={monthLabel(slip.month)} />
                  <Row2 l="Days in month" v={slip.days_in_month} />
                  <Row2 l="Present" v={`${slip.present} days`} />
                  <Row2 l="Half days" v={`${slip.half_day} (0.5 each)`} />
                  <Row2 l="Leave" v={`${slip.leave} days`} />
                  <Row2 l="Absent" v={`${slip.absent} days`} />
                  <Row2 l="Monthly salary" v={fmtRs(slip.monthly_salary)} />
                  <Row2 l="Per-day rate" v={fmtRs(slip.per_day_rate)} />
                </div>
                <div className="mt-3 pt-2 border-t border-slate-200 flex justify-between items-center">
                  <span className="font-bold text-sm">Net Payable</span>
                  <span className="font-mono-num text-xl font-extrabold text-emerald-900">{fmtRs(slip.payable)}</span>
                </div>
                <div className="mt-1 text-xs">
                  Status: <span className={`font-bold ${slip.pay_status === "paid" ? "text-emerald-700" : "text-amber-700"}`}>{slip.pay_status.toUpperCase()}</span>
                  {slip.paid_at && <span className="text-slate-500"> on {new Date(slip.paid_at).toLocaleDateString("en-IN")}</span>}
                </div>
                <div className="mt-6 flex justify-between text-[10px] text-slate-400">
                  <span>Employee signature</span><span>Authorised signature</span>
                </div>
                <div className="mt-8 border-t border-slate-200" />
                <div className="text-center text-[9px] text-slate-400 mt-2">Computer generated salary slip — trustmedspharma.com</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSlip(null)}>Close</Button>
            <Button data-testid="slip-download-btn" onClick={() => window.print()} className="bg-emerald-900 hover:bg-emerald-800">
              <Download className="w-4 h-4 mr-2" /> Download / Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row2({ l, v }) {
  return (
    <div className="flex justify-between">
      <span className="text-slate-500">{l}</span>
      <span className="font-mono-num font-medium">{v}</span>
    </div>
  );
}

const ATT_STATUS = [
  { id: "present", label: "Present", cell: "bg-emerald-600 text-white", legend: "bg-emerald-600", btn: "bg-emerald-600 hover:bg-emerald-700 text-white" },
  { id: "half_day", label: "Half Day", cell: "bg-amber-400 text-white", legend: "bg-amber-400", btn: "bg-amber-500 hover:bg-amber-600 text-white" },
  { id: "leave", label: "Leave", cell: "bg-sky-500 text-white", legend: "bg-sky-500", btn: "bg-sky-500 hover:bg-sky-600 text-white" },
  { id: "absent", label: "Absent", cell: "bg-red-50 text-red-600 border border-red-100", legend: "bg-red-400", btn: "bg-red-600 hover:bg-red-700 text-white" },
];

function AttendanceCalendar({ month, detail, daysInMonth, selectedDate, onPickDay, onQuickMark }) {
  const recMap = Object.fromEntries(detail.map((d) => [d.date, d]));
  const first = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1, 1);
  const offset = (first.getDay() + 6) % 7;
  const todayStr = format(new Date(), "yyyy-MM-dd");

  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="space-y-3" data-testid="attendance-calendar">
      <div className="flex flex-wrap gap-3 text-[11px] text-slate-600">
        {ATT_STATUS.map((s) => (
          <span key={s.id} className="flex items-center gap-1.5"><span className={`w-3 h-3 rounded ${s.legend}`} />{s.label}</span>
        ))}
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-slate-100 border border-slate-200" />Upcoming</span>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-semibold text-slate-400 uppercase">
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => <div key={d}>{d}</div>)}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const dateStr = `${month}-${String(d).padStart(2, "0")}`;
          const rec = recMap[dateStr];
          const isFuture = dateStr > todayStr;
          const status = rec?.status || (isFuture ? null : "absent");
          const st = ATT_STATUS.find((x) => x.id === status);
          const selected = selectedDate === dateStr;
          return (
            <button
              key={dateStr}
              data-testid={`att-day-${d}`}
              disabled={isFuture}
              onClick={() => onPickDay(dateStr)}
              title={rec ? `${st?.label}${rec.source === "login" ? " (auto-login)" : " (manual)"}` : st?.label || "Upcoming"}
              className={`h-10 rounded-lg text-xs font-mono-num font-bold transition-all ${
                isFuture ? "bg-slate-50 text-slate-300 cursor-not-allowed" : st?.cell
              } ${selected ? "ring-2 ring-slate-900 ring-offset-1" : ""}`}
            >
              {d}
            </button>
          );
        })}
      </div>

      <div className="pt-3 border-t border-slate-100">
        <div className="text-xs text-slate-500 mb-2">
          Mark <span className="font-mono-num font-bold text-slate-900">{selectedDate}</span> as:
        </div>
        <div className="flex gap-2 flex-wrap">
          {ATT_STATUS.map((s) => (
            <Button key={s.id} size="sm" data-testid={`quick-${s.id.replace("_", "-")}`} onClick={() => onQuickMark(s.id)} className={`h-8 ${s.btn}`}>
              {s.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
